<template>
    <div :class="`container${width < 1200 ? '-fluid' : ''} ${isScrolled ? 'scrolled' : ''}`">
        <transition-group name="toast" tag="div" class="toast-list">
            <div v-for="toast in toasts"
                 :key="toast.id"
                 class="toast-item show"
                 :class="[`toast--${toast.type || 'info'}`]"
                 @click="handleToastClick(toast)">
                <div class="toast__content">
                    <div class="toast__message">
                        {{ toast.message }}
                    </div>
                </div>
            </div>
        </transition-group>
    </div>
</template>

<script lang="ts">
import {
    computed, defineComponent, onMounted, onUnmounted, 
} from 'vue';
import CloseIcon from '@/assets/icons/close-outline.svg';
import { useToastStore, type Toast } from '@/stores/toast';
import { useUIStore } from '@/stores/ui';

export default defineComponent({
    props: {
        width: {
            type: Number,
            default: 1200,
        },
    },
    setup() {
        const toastStore = useToastStore();
        const uiStore = useUIStore();
        const toasts = computed(() => toastStore.toasts);
        const isScrolled = computed(() => uiStore.getIsScrolled);

        onMounted(() => {
            uiStore.initScrollListener();
        });

        onUnmounted(() => {
            uiStore.removeScrollListener();
        });

        function removeToast(id: string) {
            toastStore.removeToast(id);
        }

        function handleToastClick(toast: Toast) {
            // Dismiss on click
            removeToast(toast.id);
        }

        return {
            toasts,
            removeToast,
            handleToastClick,
            isScrolled,
            CloseIcon,
        };
    },
});
</script>

<style scoped lang="scss">
@import "~@/assets/variables";

.container-fluid, .container {
    position: fixed;
    left: calc(50% + 8px);
    transform: translateX(-50%);
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
    top: 100px; // Position under navbar
    z-index: 950;
    pointer-events: none;
    height: 100px;
    padding-right: 0;
    padding-left: 0;
    transition: top 0.3s ease;

    &.scrolled {
        top: 70px;
    }
}

.toast-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    max-width: 300px;
    height: 100%;
}

.toast-item {
    background: $kwast-red !important;
    color: $white !important;
    height: 40px !important;
    padding: 0 16px !important;
    display: flex !important;
    align-items: center !important;
    gap: 12px;
    pointer-events: auto !important;
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    position: relative !important;
    overflow: hidden !important;
    opacity: 1 !important;
    visibility: visible !important;

    &:hover {
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }
}

.toast__content {
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
}

.toast__message {
    font-family: "DINPro-regular", sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 16px;
    line-height: 26.08px;
    letter-spacing: 1px;
    text-align: center;
    text-transform: uppercase;
    color: $white;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
}

// Animations
.toast-enter-active {
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toast-leave-active {
    transition: all 0.3s ease-in;
}

.toast-enter-from {
    transform: translateY(-100%);
    opacity: 0;
}

.toast-leave-to {
    transform: translateY(-100%);
    opacity: 0;
}

.toast-move {
    transition: transform 0.3s ease;
}

// Mobile responsive
@media only screen and (max-width: 1200px) {
    .container-fluid, .container {
        left: 0;
        transform: none;
        top: 100px; // Adjust for mobile navbar height
        padding: 0;
        
        &.scrolled {
            top: 70px; // Same position on mobile
        }
    }

    .toast-list {
        max-width: none;
    }

    .toast-item {
        height: 40px;
        padding: 0 12px;
    }

    .toast__message {
        font-size: 16px;
        line-height: 26.08px;
        letter-spacing: 1px;
    }
}
</style>
